import { Controller, useFormContext } from "react-hook-form";

import { MongoDBConnectionAuthMethod } from "src/api/connectApi/types";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { SimpleDropDown } from "src/base-components/SimpleDropDown";
import { MongoDBUsernamePasswordAuthFieldset } from "src/connections/config/database/mongodb/MongoDBUsernamePasswordAuthFieldset";
import { isFieldErrored } from "src/connections/config/isFieldErrored";
import { errorProps } from "src/connections/config/shared/errorProps";
import { TestInvocationToggle } from "src/connections/config/shared/TestInvocationToggle";
import {
  Environment,
  MongoDBConnectionConfigInputs,
} from "src/connections/types";

type MongoDBAuthTypeSelectElement = {
  key: MongoDBConnectionAuthMethod;
  value: string;
};

const authMethods: MongoDBAuthTypeSelectElement[] = [
  { key: "username_password", value: "Username + Password" },
];

const renderAuthFields = (
  authMethod: MongoDBConnectionAuthMethod,
  environment: Environment,
) => {
  if (authMethod === "username_password") {
    return <MongoDBUsernamePasswordAuthFieldset environment={environment} />;
  }
};

type EnvironmentConfigProps = {
  environment: Environment;
  labelsPrefix: string;
};

export const MongoDBEnvironmentConfig: React.FC<EnvironmentConfigProps> = ({
  environment,
  labelsPrefix,
}) => {
  const {
    formState: { errors },
    register,
    watch,
    control,
  } = useFormContext<MongoDBConnectionConfigInputs>();
  const envPrefix = `${environment}Config` as const;
  const envErrors = errors[envPrefix];
  const enableNonProdConfigs = watch("enableNonProdConfigs");
  const authMethod = watch(`${envPrefix}.authMethod`);
  return (
    <>
      <FormItem
        gap="sm"
        helpTooltipBody={
          <div>
            The name of the MongoDB database to connect to. When in doubt, ask
            your organization's database administrator.
          </div>
        }
        label={labelsPrefix ? `${labelsPrefix} database name` : "Database name"}
        isRequired
      >
        {isFieldErrored(envErrors, "databaseName") && (
          <ErrorHint>{envErrors?.databaseName?.message}</ErrorHint>
        )}
        <Input
          data-loc={`${environment}-mongodb-connection-database-name`}
          placeholder="e.g. my-db"
          fullWidth
          {...errorProps(isFieldErrored(envErrors, "databaseName"))}
          {...register(`${envPrefix}.databaseName`, {
            required: "Database name is required",
          })}
        />
      </FormItem>
      <FormItem
        gap="sm"
        helpTooltipBody={
          <div>
            The address (hostname or IP) of the server where your MongoDB
            database is hosted. When in doubt, ask your organization's database
            administrator. Either the host or the non-SRV host string must be
            provided.
          </div>
        }
        label={labelsPrefix ? `${labelsPrefix} host` : "Host"}
      >
        {isFieldErrored(envErrors, "host") && (
          <ErrorHint>{envErrors?.host?.message}</ErrorHint>
        )}
        <Input
          data-loc={`${environment}-mongodb-connection-host`}
          placeholder="e.g. mycluster.cluster-123456789012.mongodb.net"
          fullWidth
          {...errorProps(isFieldErrored(envErrors, "host"))}
          {...register(`${envPrefix}.host`)}
        />
      </FormItem>
      <FormItem
        gap="sm"
        helpTooltipBody={
          <div>
            A set of comma-separated host:port pairs to use for the connection
            if SRV is not enabled. Only enter the string containing the host and
            port settings. Do not include username, password, or authentication
            source.
          </div>
        }
        label={
          labelsPrefix
            ? `${labelsPrefix} non-SRV host string`
            : "Non-SRV host string"
        }
      >
        {isFieldErrored(envErrors, "nonSrvHostString") && (
          <ErrorHint>{envErrors?.nonSrvHostString?.message}</ErrorHint>
        )}
        <Input
          data-loc={`${environment}-mongodb-connection-non-srv-host-string`}
          placeholder="e.g. mongodb0.example.com:27017,mongodb1.example.com:27017,mongodb2.example.com:27017"
          fullWidth
          {...errorProps(isFieldErrored(envErrors, "nonSrvHostString"))}
          {...register(`${envPrefix}.nonSrvHostString`)}
        />
      </FormItem>
      <FormItem
        gap="sm"
        helpTooltipBody={
          <div>
            The authentication source to use for the connection. Defaults to
            admin if not provided.
          </div>
        }
        label={
          labelsPrefix
            ? `${labelsPrefix} authentication source`
            : "Authentication source"
        }
      >
        {isFieldErrored(envErrors, "authSource") && (
          <ErrorHint>{envErrors?.authSource?.message}</ErrorHint>
        )}
        <Input
          data-loc={`${environment}-mongodb-connection-auth-source`}
          placeholder="e.g. admin"
          fullWidth
          {...errorProps(isFieldErrored(envErrors, "authSource"))}
          {...register(`${envPrefix}.authSource`)}
        />
      </FormItem>
      <FormItem
        description="The authentication mechanism required for the database"
        gap="sm"
        label="Select authentication type"
        isRequired
      >
        <Controller
          control={control}
          name={`${envPrefix}.authMethod`}
          render={(props) => (
            <SimpleDropDown
              buttonClassName="pl-3"
              buttonDataLoc={`${environment}-mongodb-connection-auth-type`}
              className="h-8 w-full"
              elements={authMethods}
              itemsWidth="w-full"
              placeholder="Select authentication type"
              placement="bottomLeft"
              selectedKey={String(props.field.value)}
              onSelect={(value) => {
                props.field.onChange(value);
              }}
            />
          )}
        />
      </FormItem>
      {renderAuthFields(authMethod, environment)}
      {environment === "production" && (
        <TestInvocationToggle<MongoDBConnectionConfigInputs>
          control={control}
          enableNonProdConfigs={enableNonProdConfigs}
          type="database"
        />
      )}
    </>
  );
};
